import clsx from "clsx";
import { useResizeObserver } from "@wojtekmaj/react-hooks";
import { useCallback, useMemo, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import Skeleton from "react-loading-skeleton";
import { useTranslation } from "react-i18next";

import classes from "./styles.module.scss";

import type { IPdfViewerProps } from ".";

import { Button } from "@components/atoms/button";
import { Icon } from "@components/atoms/icons";

export function PdfViewer({ src, isPreview, onShow }: IPdfViewerProps) {
  const { t } = useTranslation("actions");

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>();

  const onResize = useCallback<ResizeObserverCallback>((entries) => {
    const [entry] = entries;

    if (entry) {
      setContainerWidth(entry.contentRect.width);
    }
  }, []);

  useResizeObserver(containerRef?.current, {}, onResize);

  const [numPages, setNumPages] = useState<number | null>(null);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const loader = useMemo(() => <Skeleton width={"100%"} height={"20vh"} />, []);

  return (
    <div
      className={clsx(classes.pdfViewer, isPreview && classes.pdfRendererFull)}
    >
      <div
        className={clsx(
          classes.pdfContainer,
          isPreview && classes.pdfRendererFull,
        )}
        ref={containerRef}
      >
        <Document
          className={clsx(classes.pdfRendererFull)}
          file={src}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={loader}
        >
          {isPreview ? (
            <Page
              key={`page_0`}
              pageNumber={1}
              width={containerWidth || undefined}
              loading={loader}
            />
          ) : (
            Array.from({ length: numPages || 0 }, (_, index) => (
              <Page
                key={`page_${index}`}
                pageNumber={index + 1}
                width={containerWidth || undefined}
                loading={loader}
              />
            ))
          )}
        </Document>
      </div>
      {isPreview && (
        <Button
          mode={"main"}
          label={t("showPDF")}
          icon={<Icon name={"Eye"} />}
          onClick={() => onShow?.()}
        />
      )}
    </div>
  );
}
